import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  IconWhatsApp, IconCheck, IconAlertTriangle, IconRefresh,
  IconX, IconPlus, IconInfo,
} from "../components/icons";

// ── tipos ─────────────────────────────────────────────────────────────────────

type InstanceStatus = "connected" | "disconnected" | "pending" | "error";

type WhatsappInstance = {
  id: string;
  instanceName: string;
  evolutionBaseUrl: string;
  status: InstanceStatus;
  webhookUrl: string | null;
  hasApiKey: boolean;
  createdAt: string;
};

type HealthResult = {
  ok: boolean;
  status?: InstanceStatus;
  evolutionState?: string;
  error?: string;
  detail?: string;
};

// ── helpers visuais ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<InstanceStatus, {
  label: string;
  dot: string;
  badge: string;
  pulse?: boolean;
}> = {
  connected:    { label: "Conectado",     dot: "bg-tm-500",       badge: "bg-tm-50 text-tm-700 border-tm-200",       pulse: true },
  disconnected: { label: "Desconectado",  dot: "bg-danger-500",   badge: "bg-danger-50 text-danger-700 border-danger-200" },
  pending:      { label: "Pendente",      dot: "bg-warning-500",  badge: "bg-warning-50 text-warning-700 border-warning-200" },
  error:        { label: "Erro",          dot: "bg-danger-600",   badge: "bg-danger-50 text-danger-800 border-danger-300" },
};

function StatusBadge({ status }: { status: InstanceStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.error;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? "animate-pulse" : ""}`} />
      {cfg.label}
    </span>
  );
}

// ── formulário de nova instância ──────────────────────────────────────────────

function NewInstanceForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({ instanceName: "", evolutionBaseUrl: "", apiKey: "" });

  const create = useMutation({
    mutationFn: (data: typeof form) => api.post("/whatsapp/instances", data),
    onSuccess,
  });

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="card p-5 border-tm-200 border-2">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-gray-900">Nova instância Evolution</p>
        <button onClick={onCancel} className="btn-ghost p-1.5 text-gray-400">
          <IconX className="w-4 h-4" />
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); create.mutate(form); }} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome da Instância *</label>
            <input
              required type="text"
              value={form.instanceName}
              onChange={(e) => set("instanceName", e.target.value)}
              placeholder="ex: transmasi_bot"
              className="w-full text-sm border border-surface-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400"
            />
            <p className="text-xs text-gray-400 mt-0.5">Mesmo nome configurado na Evolution API</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Base URL da Evolution *</label>
            <input
              required type="url"
              value={form.evolutionBaseUrl}
              onChange={(e) => set("evolutionBaseUrl", e.target.value)}
              placeholder="https://evolutionapi.seudominio.com"
              className="w-full text-sm border border-surface-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Global API Key *</label>
          <input
            required type="password"
            value={form.apiKey}
            onChange={(e) => set("apiKey", e.target.value)}
            placeholder="API key do painel da Evolution"
            className="w-full text-sm border border-surface-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400"
          />
          <p className="text-xs text-gray-400 mt-0.5">Armazenada com criptografia AES-256-GCM</p>
        </div>

        {create.isError && (
          <p className="text-sm text-danger-600 flex items-center gap-1.5">
            <IconAlertTriangle className="w-4 h-4" />
            {(create.error as Error).message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={create.isPending} className="btn-primary">
            {create.isPending ? "Salvando…" : "Salvar instância"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── card de instância ─────────────────────────────────────────────────────────

function InstanceCard({ inst, onRefresh }: { inst: WhatsappInstance; onRefresh: () => void }) {
  const [healthResult, setHealthResult] = useState<HealthResult | null>(null);
  const [checking, setChecking] = useState(false);

  const checkHealth = async () => {
    setChecking(true);
    setHealthResult(null);
    try {
      const result = await api.get<HealthResult>(`/whatsapp/instances/${inst.id}/health`);
      setHealthResult(result);
      onRefresh(); // atualiza lista para refletir novo status
    } catch (err) {
      setHealthResult({ ok: false, error: (err as Error).message });
    } finally {
      setChecking(false);
    }
  };

  const status: InstanceStatus = inst.status as InstanceStatus ?? "pending";
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;

  return (
    <div className="card overflow-hidden">
      {/* Barra de status */}
      <div className={`h-1 ${
        status === "connected" ? "bg-tm-500"
        : status === "disconnected" ? "bg-danger-500"
        : status === "pending" ? "bg-warning-400"
        : "bg-danger-600"
      }`} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              status === "connected" ? "bg-tm-100" : "bg-gray-100"
            }`}>
              <IconWhatsApp className={`w-5 h-5 ${status === "connected" ? "text-tm-600" : "text-gray-400"}`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{inst.instanceName}</p>
              <p className="text-xs text-gray-400 truncate max-w-[180px]">{inst.evolutionBaseUrl}</p>
            </div>
          </div>
          <StatusBadge status={status} />
        </div>

        {/* Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">API Key</span>
            <span className={`font-medium ${inst.hasApiKey ? "text-tm-600" : "text-danger-600"}`}>
              {inst.hasApiKey ? "✓ Configurada" : "✗ Não configurada"}
            </span>
          </div>
          {inst.webhookUrl && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Webhook</span>
              <span className="text-gray-700 truncate max-w-[160px] font-mono">{inst.webhookUrl}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Criado em</span>
            <span className="text-gray-600">
              {new Date(inst.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })}
            </span>
          </div>
        </div>

        {/* Health check result */}
        {healthResult && (
          <div className={`rounded-lg px-3 py-2 mb-3 text-xs ${
            healthResult.ok ? "bg-tm-50 border border-tm-200 text-tm-800"
            : "bg-danger-50 border border-danger-200 text-danger-800"
          }`}>
            {healthResult.ok
              ? `✓ Conexão OK — Evolution: "${healthResult.evolutionState}" → ${cfg.label}`
              : `✗ ${healthResult.error ?? "Falha"}: ${healthResult.detail ?? ""}`}
          </div>
        )}

        {/* Actions */}
        <button
          onClick={checkHealth}
          disabled={checking}
          className="w-full btn-secondary text-xs py-2 justify-center"
        >
          <IconRefresh className={`w-3.5 h-3.5 ${checking ? "animate-spin" : ""}`} />
          {checking ? "Verificando conexão…" : "Testar conexão"}
        </button>
      </div>
    </div>
  );
}

// ── instrução de webhook ──────────────────────────────────────────────────────

function WebhookInstructions() {
  const apiUrl = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");
  const webhookUrl = apiUrl.startsWith("http")
    ? `${apiUrl}/webhooks/evolution`
    : `${window.location.origin}${apiUrl}/webhooks/evolution`;

  const copy = () => navigator.clipboard?.writeText(webhookUrl).catch(() => {});

  return (
    <div className="card p-5 border-info-100">
      <div className="flex items-start gap-3">
        <IconInfo className="w-4 h-4 text-info-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 mb-1">Como configurar o webhook na Evolution</p>
          <p className="text-xs text-gray-600 mb-3 leading-relaxed">
            Para receber mensagens do WhatsApp, configure o seguinte endpoint como webhook na Evolution API
            (Configurações → Webhook → Global ou por instância):
          </p>
          <div className="flex items-center gap-2 bg-surface-muted rounded-lg px-3 py-2 border border-surface-border">
            <code className="text-xs text-gray-700 flex-1 truncate font-mono">{webhookUrl}</code>
            <button onClick={copy} className="btn-ghost p-1 flex-shrink-0 text-gray-400 hover:text-tm-600" title="Copiar">
              <IconCheck className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Eventos necessários: <code className="bg-gray-100 px-1 rounded">MESSAGES_UPSERT</code>
          </p>
        </div>
      </div>
    </div>
  );
}

// ── instrução de número de telefone ──────────────────────────────────────────

function PhoneNumberNote() {
  return (
    <div className="card p-5 border-warning-100">
      <div className="flex items-start gap-3">
        <IconAlertTriangle className="w-4 h-4 text-warning-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-1">Vincule o número do gestor ao usuário</p>
          <p className="text-xs text-gray-600 leading-relaxed">
            Para receber mensagens, o número de WhatsApp do gestor deve estar cadastrado no perfil do usuário
            (campo <code className="bg-gray-100 px-1 rounded text-xs">whatsappNumber</code>).
            Formato: <code className="bg-gray-100 px-1 rounded text-xs">+5548999991234</code> (com DDI e DDD).
            Peça ao administrador para atualizar via API ou diretamente no banco.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function WhatsappInstancesPage() {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const { data, isLoading } = useQuery<{ items: WhatsappInstance[] }>({
    queryKey: ["whatsapp-instances"],
    queryFn: () => api.get("/whatsapp/instances"),
    staleTime: 30_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["whatsapp-instances"] });

  const syncAll = async () => {
    setSyncing(true);
    try {
      await api.post("/whatsapp/instances/sync-all-status");
      refresh();
    } finally {
      setSyncing(false);
    }
  };

  const instances = data?.items ?? [];
  const connectedCount = instances.filter((i) => i.status === "connected").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Instâncias WhatsApp</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Gerencie as instâncias da Evolution API.
            {instances.length > 0 && ` ${connectedCount}/${instances.length} conectada${connectedCount !== 1 ? "s" : ""}.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {instances.length > 0 && (
            <button
              onClick={syncAll}
              disabled={syncing}
              className="btn-secondary text-xs py-2"
            >
              <IconRefresh className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando…" : "Sincronizar todas"}
            </button>
          )}
          <button
            onClick={() => setIsAdding(!isAdding)}
            className="btn-primary text-xs py-2"
          >
            {isAdding ? <IconX className="w-3.5 h-3.5" /> : <IconPlus className="w-3.5 h-3.5" />}
            {isAdding ? "Cancelar" : "Nova instância"}
          </button>
        </div>
      </div>

      {/* Formulário de criação */}
      {isAdding && (
        <NewInstanceForm
          onSuccess={() => { setIsAdding(false); refresh(); }}
          onCancel={() => setIsAdding(false)}
        />
      )}

      {/* Lista de instâncias */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="card p-5 animate-pulse space-y-3">
              <div className="h-3 w-24 bg-gray-200 rounded" />
              <div className="h-3 w-40 bg-gray-100 rounded" />
              <div className="h-8 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : instances.length === 0 && !isAdding ? (
        <div className="py-12 text-center border-2 border-dashed border-surface-border rounded-xl">
          <IconWhatsApp className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">Nenhuma instância configurada</p>
          <p className="text-xs text-gray-400 mt-1">Clique em "Nova instância" para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {instances.map((inst) => (
            <InstanceCard key={inst.id} inst={inst} onRefresh={refresh} />
          ))}
        </div>
      )}

      {/* Instruções */}
      {instances.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <WebhookInstructions />
          <PhoneNumberNote />
        </div>
      )}
    </div>
  );
}
